import { type ApiErrorBody } from '@ibe/types';
import { type ZodError } from 'zod';

/**
 * The three ways a request can fail, kept as three types.
 *
 * The distinction is not pedantry — it decides what the user is told. The
 * server answering "no" is often the user's problem and always the server's
 * considered position. The network never answering is nobody's fault and is
 * worth retrying. A response that does not match its contract is *our* bug,
 * and telling a user they did something wrong would be a lie.
 *
 * This mirrors the platform's own operational/non-operational split in
 * `packages/errors`, deliberately: the same reasoning applies on both sides
 * of the wire.
 */

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly correlationId: string;
  /**
   * Field errors keyed by field.
   *
   * The wire carries an array; every consumer wants a lookup — react-hook-form
   * sets one field at a time. Flattened once here rather than at each call site.
   */
  readonly fieldErrors: Readonly<Record<string, string>>;

  constructor(status: number, body: ApiErrorBody) {
    super(body.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.code;
    this.correlationId = body.correlationId;
    const grouped = (body.fieldErrors ?? []).reduce<Record<string, string[]>>((acc, error) => {
      (acc[error.field] ??= []).push(error.message);
      return acc;
    }, {});
    this.fieldErrors = Object.fromEntries(
      Object.entries(grouped).map(([field, messages]) => [field, messages.join('; ')]),
    );
  }
}

/** The network never answered. Distinct from the server answering with an error. */
export class NetworkError extends Error {
  constructor(cause: unknown) {
    super('Could not reach the server. Check your connection and try again.', { cause });
    this.name = 'NetworkError';
  }
}

/**
 * A response did not match the schema the client expects.
 *
 * Raised at the API boundary rather than allowed through, because the
 * alternative is `undefined` surfacing three components deep as a blank table
 * cell that no test catches and no user reports. Failing loudly at the seam
 * turns a silent data defect into a locatable one.
 *
 * Never shown to a user as their mistake: this is a deployment or contract
 * problem, so it logs the detail and the UI says only that something went
 * wrong.
 */
export class ContractError extends Error {
  readonly endpoint: string;
  /** Dotted paths that failed, for the log line. */
  readonly issuePaths: readonly string[];

  constructor(endpoint: string, cause: ZodError) {
    const paths = cause.issues.map((issue) => issue.path.join('.'));

    super(`Response from ${endpoint} did not match its contract: ${paths.join(', ')}`, {
      cause,
    });
    this.name = 'ContractError';
    this.endpoint = endpoint;
    this.issuePaths = paths;
  }
}
